import React, { useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Stack,
  Box,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/IosShare'; // iOS‑style share icon
import AddBoxIcon from '@mui/icons-material/AddBoxOutlined';
import { safeLocalStorage } from 'utils/safeStorage';

/**
 * AddToHomeScreenPrompt
 * ------------------------------------------------------------------
 * A reusable PWA "Add to Home Screen" prompt for **iOS Safari** only.
 *
 * Behaviour
 * =========
 * • Detects iOS Safari that is **not** already running in standalone mode.
 * • Shows the dialog on first visit; remembers dismissal for X days.
 * • Design mimics native iOS modal, themed with MUI.
 * • Focuses on accessibility & responsiveness.
 * ------------------------------------------------------------------
 *
 * Props
 * -----
 * daysToHide   – number How many days to wait before showing again.
 * forceShow    – boolean Ignore storage & always show (useful dev prop).
 */
const AddToHomeScreenIOSPrompt = ({ daysToHide = 15, forceShow = false }) => {
  const [open, setOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // Utilities
  const isiOS = () => {
    const ua = window.navigator.userAgent;
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  };

  const isInStandaloneMode = () =>
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;

  const storageKey = 'palco.addToHomeScreen.dontShowTill';

  // ---------------------------------------------------------------------------
  // Effect: decide whether the dialog should appear
  useEffect(() => {
    if (!isiOS() || isInStandaloneMode()) return; // not iOS Safari or already installed

    const dontShowTill = safeLocalStorage.getItem(storageKey);
    const now = Date.now();

    if (forceShow || !dontShowTill || Number(dontShowTill) < now) {
      // Give Safari a brief moment to settle; avoids showing BEFORE initial page paint
      const timer = setTimeout(() => setOpen(true), 600);
      return () => clearTimeout(timer);
    }
  }, [forceShow]);

  // ---------------------------------------------------------------------------
  // Handlers
  const handleClose = useCallback(() => {
    setOpen(false);
    const nextTime = Date.now() + daysToHide * 24 * 60 * 60 * 1000;
    safeLocalStorage.setItem(storageKey, nextTime.toString());
  }, [daysToHide]);

  // ---------------------------------------------------------------------------
  // Render
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: (theme) => ({
          backgroundColor: theme.palette.mode === 'dark' ? '#111' : '#fff',
          borderRadius: 3,
          p: 1,
        }),
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ p: 1, pl: 2, fontWeight: 600 }}>
        Install (Add to Home Screen)
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This website has app functionality. Add it to your home screen to use
          it in fullscreen and while offline.
        </Typography>

        <Stack direction="row" spacing={2} justifyContent="center">
          <Stack alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <ShareIcon fontSize="medium" />
            </Box>
            <Typography variant="caption">
              1) Press the "Share" button
            </Typography>
          </Stack>

          <Stack alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 44,
                height: 44,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                bgcolor: 'action.hover',
              }}
            >
              <AddBoxIcon fontSize="medium" />
            </Box>
            <Typography variant="caption">
              2) Press "Add to Home Screen"
            </Typography>
          </Stack>
        </Stack>

        {/* Fallback close button for VoiceOver users */}
        <Button
          onClick={handleClose}
          fullWidth
          variant="text"
          size="small"
          sx={{ mt: 3, textTransform: 'none' }}
        >
          Close
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default AddToHomeScreenIOSPrompt;
